import { CheckCircle2, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '../store'
import Spinner from '../components/Spinner'
import { login } from '../components/UserMenu'
import { SUPPORT_EMAIL } from '../lib/contact'
import './VerifyEmailPage.css'

function isVerifiedSuccess(searchParams: URLSearchParams): boolean {
  if (searchParams.get('success') === 'true' || searchParams.get('code') === 'success') return true
  const message = searchParams.get('message')?.toLowerCase() ?? ''
  return message.includes('email was verified') || message.includes('email verified')
}

// Shown when Auth0 denies login (check your email) or after clicking the verification link (success).
export default function VerifyEmailPage() {
  const dispatch = useDispatch<AppDispatch>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || '/'
  const verified = isVerifiedSuccess(searchParams)

  // Auth0's logout hop lands back here with signIn=1 to resume the login it just cleared the session
  // for. Reading it from the URL means the redirecting state renders on the first paint, so the card
  // below never appears on the way to the login screen.
  const resumeSignIn = searchParams.get('signIn') === '1'
  const [leavingToSignIn, setLeavingToSignIn] = useState(resumeSignIn)

  useEffect(() => {
    if (!resumeSignIn) return
    login(dispatch, returnTo, { prompt: 'login' })
  }, [dispatch, resumeSignIn, returnTo])

  if (leavingToSignIn) {
    return (
      <div className="verify-email-page">
        <div className="verify-email-page__card" data-testid="verify-email-redirecting">
          <div className="verify-email-page__brand">
            <img src="/images/logo.svg" alt="" width="26" height="30" className="verify-email-page__logo" />
            <span className="verify-email-page__wordmark">Replay QA</span>
          </div>
          <p className="verify-email-page__lead verify-email-page__redirecting">
            <Spinner size="md" /> Taking you to sign in…
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="verify-email-page">
      <div className="verify-email-page__card" data-testid="verify-email-page">
        <div className="verify-email-page__brand">
          <img src="/images/logo.svg" alt="" width="26" height="30" className="verify-email-page__logo" />
          <span className="verify-email-page__wordmark">Replay QA</span>
        </div>

        <div className="verify-email-page__icon" aria-hidden>
          {verified ? <CheckCircle2 size={24} strokeWidth={1.75} /> : <Mail size={24} strokeWidth={1.75} />}
        </div>

        <h1 className="verify-email-page__title">
          {verified ? 'Email verified' : 'Check your email'}
        </h1>
        <p className="verify-email-page__lead">
          {verified
            ? 'Your email address is confirmed. Sign in to finish setting up your Replay QA account.'
            : 'We sent you a verification link. Open it to confirm your email address, then come back here and sign in.'}
        </p>

        {!verified && (
          <div className="verify-email-page__steps">
            <ol>
              <li>Check your inbox for an email from Replay.</li>
              <li>Click <strong>Verify email address</strong> in that email.</li>
              <li>Return here and sign in with the same email and password.</li>
            </ol>
          </div>
        )}

        <button
          type="button"
          className="verify-email-page__btn"
          data-testid="verify-email-sign-in"
          onClick={() => {
            setLeavingToSignIn(true)
            login(dispatch, returnTo, { prompt: 'login', freshSession: true })
          }}
        >
          {verified ? 'Sign in' : 'Back to sign in'}
        </button>

        {!verified && (
          <p className="verify-email-page__footer">
            Didn&apos;t get the email? Check spam and wait a few minutes. Once you&apos;ve clicked the
            link, use <strong>Back to sign in</strong> above. Questions?{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        )}
      </div>
    </div>
  )
}
